import { useCallback, useEffect, useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { isAxiosError } from "axios";
import { Heading } from "react-aria-components";
import { X } from "lucide-react";
import RemixMark from "@/app/routes/dashboard/components/icons/RemixMark";
import { KitModal as Modal } from "@/app/routes/dashboard/components/kit/Modal";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitIconButton, KitKeyCap, KitWalkButton } from "@/app/routes/dashboard/components/kit/IconButton";
import { KitTag } from "@/app/routes/dashboard/components/kit/Tag";
import { EmptyState, ErrorState } from "@/app/routes/dashboard/components/ViewStates";
import type { PlateArt } from "@/app/routes/dashboard/components/Plate";
import { queue } from "@/components/Toast";
import { usePermissions } from "@/hooks/usePermissions";
import type { TrendDetail } from "@/types/trend.type";
import { useTrendDetail } from "../api/useTrends";
import {
  REFERENCES_PATH,
  saveErrorMessage,
  showAlreadySaved,
  useSavedTrendIds,
  useSaveTarget,
  useSaveTrendToReferences,
} from "../api/useSaveTrendToReferences";
import TrendTypeBadge from "./TrendTypeBadge";
import TrendSubmissionCarousel from "./TrendSubmissionCarousel";
import {
  compactAge,
  compactTrendNum,
  countedFigures,
  evidenceCoverage,
  evidenceSentence,
  languageLabel,
  originLabel,
  platformLabel,
  recreatePrompt,
  regionLabel,
  spokenAge,
  spokenTrendNum,
  trendPlatforms,
  trendStats,
} from "./trendMeta";

/**
 * Moving through the wall without closing the sheet: where this trend sits
 * among the trends loaded on the wall's current page, in wall order, and the
 * way to its neighbours. A null move is an end of the loaded page; the sheet
 * never fetches another page to keep going.
 */
export interface TrendDetailNav {
  /** 0-based position on the loaded page. */
  index: number;
  count: number;
  onPrevious: (() => void) | null;
  onNext: (() => void) | null;
}

/** Where a J or K press is text being typed rather than a request to move. */
function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) {
    return !["button", "checkbox", "radio", "range", "submit", "reset", "color", "file"].includes(target.type);
  }
  return false;
}

/** The position and the two moves, in the header beside Close: the library's
 *  Button / Walk, the word and its key; a phone keeps only the chevrons. */
function TrendNav({ nav }: { nav: TrendDetailNav }) {
  return (
    <>
      <span className="td-head-label mr-4 whitespace-nowrap">
        {nav.index + 1} of {nav.count}
      </span>
      <KitWalkButton
        direction="previous"
        className="td-nav-step"
        disabled={!nav.onPrevious}
        onClick={() => nav.onPrevious?.()}
        aria-label="Previous trend"
        aria-keyshortcuts="k"
      >
        <span className="hidden md:inline">Previous</span>
        <KitKeyCap className="hidden md:inline-flex">K</KitKeyCap>
      </KitWalkButton>
      <KitWalkButton
        direction="next"
        className="td-nav-step"
        disabled={!nav.onNext}
        onClick={() => nav.onNext?.()}
        aria-label="Next trend"
        aria-keyshortcuts="j"
      >
        <span className="hidden md:inline">Next trend</span>
        <KitKeyCap className="hidden md:inline-flex">J</KitKeyCap>
      </KitWalkButton>
    </>
  );
}

export interface TrendDetailPreview {
  trend: TrendDetail | null;
  isLoading: boolean;
  isError: boolean;
  standIn?: PlateArt;
}

/** The route answered that there is no such trend: it was removed from the
 *  library, or the link names one that never existed. Only a 404 says that; a
 *  failed read of any other kind is a read to try again. */
function isNotFound(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

/** Day first, as the sheet's other dates read in the Gulf: "20 Sep 2026". */
function formatDate(iso: string | null): string {
  if (!iso) return "Not recorded";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** The comp's action size: 32px high, 13px label, 14px side padding. */
const ACTION_SIZE = "h-8 px-3.5 text-[13px]";

/** A section of the reading column: the name, then a hairline to the edge. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id}>
      <h3 id={id} className="td-rule">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** One trend-level figure: a floor drawn large, and what it counts under it. */
function TrendFigure({ label, value, floor }: { label: string; value: number | null; floor: boolean }) {
  return (
    <div className="td-fig">
      <dt className="td-label">{label}</dt>
      <dd className="td-fig-value">
        {value === null ? (
          <span className="td-label">Not counted</span>
        ) : (
          <>
            <span aria-hidden="true">
              {compactTrendNum(value)}
              {floor ? "+" : ""}
            </span>
            <span className="sr-only">
              {floor ? `at least ${spokenTrendNum(value)}` : String(value)}
            </span>
          </>
        )}
      </dd>
    </div>
  );
}

/**
 * EVIDENCE: what the harvest counted across the trend's examples.
 *
 * Views and likes are FLOORS and carry a "+": the aggregate adds nothing for an
 * example whose count never came back. A total of zero is NOT COUNTED, never a
 * measured zero. An exampleCount of 0 draws no figures at all, because three
 * 30px zeros would be the sheet asserting counts nobody took.
 */
function Evidence({ trend }: { trend: TrendDetail }) {
  const { exampleCount } = trendStats(trend);
  const { views, likes } = countedFigures(trend);
  const coverage = evidenceCoverage(exampleCount, trend.examples);

  if (exampleCount === 0) {
    return (
      <Section title="Evidence">
        <p className="td-caption">
          {evidenceSentence(trend)} There are no view or like figures to show, because there was
          nothing to count them on.
        </p>
      </Section>
    );
  }

  return (
    <Section title="Evidence">
      <dl className="td-figures">
        <TrendFigure label="Views" value={views} floor />
        <TrendFigure label="Likes" value={likes} floor />
        <TrendFigure label="Examples" value={exampleCount} floor={false} />
      </dl>
      {/* Only where there IS a figure: with views and likes both uncounted,
          calling nothing "the creators' figures" would qualify a number the
          sheet never drew. */}
      {views !== null || likes !== null ? (
        <p className="td-note">
          The creators&rsquo; figures, not your brand&rsquo;s, read by the discovery run that last
          found this trend.
        </p>
      ) : null}
      {coverage ? <p className="td-note">{coverage}</p> : null}
    </Section>
  );
}

/** WHERE IT WAS FOUND: the four facts about the harvest, not about any brand. */
function Found({ trend }: { trend: TrendDetail }) {
  /* The card's rule, so the sheet never names a platform the card did not. */
  const platforms = trendPlatforms(trend);
  return (
    <Section title="Where it was found">
      <dl className="td-meta">
        <div>
          <dt className="td-label">Platforms</dt>
          <dd>{platforms ? platforms.map(platformLabel).join(", ") : "Not recorded"}</dd>
        </div>
        <div>
          <dt className="td-label">Discovered</dt>
          <dd>{formatDate(trend.createdAt)}</dd>
        </div>
        <div>
          <dt className="td-label">Region</dt>
          <dd>{trend.region ? regionLabel(trend.region) : "Not set"}</dd>
        </div>
        <div>
          <dt className="td-label">Language</dt>
          <dd>{trend.language ? languageLabel(trend.language) : "Not set"}</dd>
        </div>
      </dl>
    </Section>
  );
}

/**
 * Save to references, from the sheet. The same path, the same optimistic saved
 * set and the same rollback as the plate's Save. Not drawn
 * where it cannot work: in the fixture harness (no organization to file into),
 * or with no brand picked.
 */
function SaveAction({ trend, live }: { trend: TrendDetail; live: boolean }) {
  const navigate = useNavigate();
  const save = useSaveTrendToReferences();
  const target = useSaveTarget();
  const saved = useSavedTrendIds().includes(trend.id);
  if (!live || target === null) return null;

  return (
    <KitButton
      variant="secondary"
      className={ACTION_SIZE}
      onClick={() => {
        if (saved) {
          showAlreadySaved(() => navigate(REFERENCES_PATH));
          return;
        }
        save.mutateAsync(trend).catch((error: unknown) => {
          queue.add({ title: "Could not save that", description: saveErrorMessage(error), variant: "error" });
        });
      }}
    >
      {/* "Save" on the face, as the library sheet prints it; the name keeps
          where it goes. */}
      {saved ? "Saved" : "Save"}
      <span className="sr-only"> to references</span>
    </KitButton>
  );
}

/** The loading face, shaped like the sheet: a stage, a title, a reading. */
function DetailSkeleton() {
  return (
    <div className="td-layout" aria-hidden="true">
      <div className="td-stage-col">
        <div className="tsc-frame">
          <div className="tsc-track">
            <div className="tsc-slide">
              <div className="trend-plate tsc-plate co-plate-loading" />
            </div>
          </div>
        </div>
        {/* The row under the frame, empty: the stage column, and so the sheet,
            is already the height the loaded trend will have. */}
        <div className="tsc-controls" />
      </div>
      <div className="td-reading">
        <span className="td-bone h-[30px] w-[88%]" />
        <span className="td-bone mt-1.5 h-[30px] w-[52%]" />
        <span className="td-bone mt-5 h-3 w-full max-w-[68ch]" />
        <span className="td-bone mt-2 h-3 w-[94%] max-w-[64ch]" />
        <span className="td-bone mt-2 h-3 w-[70%]" />
        <span className="td-bone mt-7 h-8 w-[120px]" />
      </div>
    </div>
  );
}

/**
 * The trend, opened: sheet v2.
 *
 * The header says what the trend is (kind, where its examples were found, how
 * long ago) and where it sits on the wall. The body is two columns: the stage of
 * examples on the left, and on the right the trend's own reading, the two next
 * actions, what the harvest counted, the example on the stage, and where the
 * examples were found. Trend facts and example facts never share a block.
 *
 * Every number in here is the creators', a snapshot from the discovery run that
 * last saw the trend, and a floor. The sheet says so beside the figures rather
 * than leaving it to be inferred.
 */
export default function TrendDetailModal({
  trendId,
  onClose,
  preview,
  nav,
}: {
  trendId: string | null;
  onClose: () => void;
  preview?: TrendDetailPreview;
  /** Present when the open trend is on the wall's loaded page. */
  nav?: TrendDetailNav;
}) {
  /* J and K move to the next and the previous trend while the sheet is open,
     unless focus is in something being typed into. The arrow keys are not
     touched: they belong to the carousel of examples. */
  useEffect(() => {
    if (!trendId || !nav) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTextEntry(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === "j" && nav.onNext) {
        event.preventDefault();
        nav.onNext();
      } else if (key === "k" && nav.onPrevious) {
        event.preventDefault();
        nav.onPrevious();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [trendId, nav]);

  // With a preview supplied the query is switched off at the source, so the
  // harness never depends on a backend that is not there.
  const query = useTrendDetail(preview ? null : trendId);
  const trend = preview ? preview.trend : (query.data ?? null);
  const isLoading = preview ? preview.isLoading : query.isLoading;
  const isError = preview ? preview.isError : query.isError;
  const notFound = !preview && isNotFound(query.error);
  const standIn = preview?.standIn;
  const navigate = useNavigate();
  // Recreate opens a Workshop project, which Viewers can't create.
  const { canUseWorkshop } = usePermissions();

  /* A new trend starts at the top of the sheet: J from the foot of one trend's
     reading must not land half way down the next one's. */
  const bodyRef = useRef<HTMLDivElement>(null);
  const shownId = trend?.id ?? null;
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [shownId]);

  const origin = trend ? originLabel(trend) : null;

  /* Card → detail: the sheet grows out of the trend's plate on the wall and,
     closing, goes back into the plate of the trend it shows then. The last
     open trend is kept, because closing has already cleared trendId. */
  const lastTrendId = useRef(trendId);
  useLayoutEffect(() => {
    if (trendId) lastTrendId.current = trendId;
  }, [trendId]);
  const growFrom = useCallback(() => {
    const id = lastTrendId.current;
    if (!id) return null;
    return document.querySelector(`[data-trend-id="${CSS.escape(id)}"] .trend-plate`);
  }, []);

  /* Built once per trend and handed to the carousel as elements, so a swipe
     between examples never re-renders the trend's own blocks. */
  const lead = trend ? (
    <>
      <Heading slot="title" level={2} dir="auto" className="td-title">
        {trend.title}
      </Heading>
      {trend.body ? (
        <p dir="auto" className="td-read">
          {trend.body}
        </p>
      ) : null}
      {trend.tags.length > 0 ? (
        <p className="td-tags">
          <span className="sr-only">Tags: </span>
          {trend.tags.join(" · ")}
        </p>
      ) : null}
      <div className="td-actions">
        {/* This only carries a prepared draft into Workshop; the sheet saves
            and sends nothing itself. */}
        {canUseWorkshop ? (
          <KitButton
            iconLeft={RemixMark}
            className={ACTION_SIZE}
            onClick={() =>
              navigate("/dashboard/workshop", { state: { draftPrompt: recreatePrompt(trend) } })
            }
          >
            Recreate
          </KitButton>
        ) : null}
        <SaveAction trend={trend} live={!preview} />
      </div>
      <Evidence trend={trend} />
    </>
  ) : null;
  const tail = trend ? <Found trend={trend} /> : null;

  return (
    <Modal
      isOpen={!!trendId}
      growFrom={growFrom}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      hideCloseButton
      /* Named by the title heading once a trend is on screen; until then the
         dialog still needs a name. */
      aria-label={trend ? undefined : "Trend"}
      /* A SHEET on a phone, floating 8px in from the sides and the foot with
         all four corners at radius 16, and an opaque DIALOG from md up, 1040
         wide. A reading layer (library Trends / Detail, style B): solid, no
         hairline. The header stays put and the body is the one thing that
         scrolls. */
      overlayClassName="!p-2 items-end md:!p-4 md:items-center"
      /* The height is td-sheet's (trendWall.css): the stage column's, never
         the trend's reading, so J and K never resize the sheet. */
      modalClassName="td-sheet !flex !w-full !max-w-none !flex-col !overflow-hidden !p-0 md:!w-[min(calc(100vw-2rem),1040px)] md:!max-w-[1040px]"
    >
      <header className="td-head">
        <div className="td-head-meta">
          {trend ? (
            <>
              <TrendTypeBadge type={trend.type} />
              {trend.isBoosted ? <KitTag tone="ink">Boosted by an editor</KitTag> : null}
              {origin ? <span className="td-head-label">{origin}</span> : null}
              <span className="td-head-label td-head-label--faint">
                <span aria-hidden="true">Found {compactAge(trend.createdAt)} ago</span>
                <span className="sr-only">Found {spokenAge(trend.createdAt)}</span>
              </span>
            </>
          ) : isLoading ? (
            <span className="td-bone h-6 w-24" aria-hidden="true" />
          ) : null}
        </div>
        <div className="td-head-nav">
          {nav ? <TrendNav nav={nav} /> : null}
          <KitIconButton kind="close" icon={X} aria-label="Close" onClick={onClose} className="td-close" />
        </div>
      </header>

      <div ref={bodyRef} className="td-body">
        {/* The skeleton is drawn for the eye and hidden from a screen reader, so
            the wait is said here instead. Always mounted, so the line is heard
            when it changes rather than lost with a region that arrives full. */}
        <p className="sr-only" role="status">
          {isLoading ? "Loading the trend" : ""}
        </p>
        {isLoading ? (
          <DetailSkeleton />
        ) : notFound ? (
          /* The route answered, and the answer is that the trend is gone. Trying
             again would ask the same question and get the same answer, so the
             only way on is Close. */
          <EmptyState minHeight={420} title="This trend is no longer in the library" />
        ) : isError || !trend ? (
          <ErrorState
            minHeight={420}
            title="This trend did not load"
            body="Nothing was shown rather than guessed. The wall behind it is unchanged."
            onRetry={preview ? undefined : () => void query.refetch()}
          />
        ) : trend.examples.length === 0 ? (
          /* Nothing came back, so there is no stage: the trend's reading is all
             there is, and EVIDENCE says why. Inventing a stage out of the
             trend's preview clip would show an example the route never sent. */
          /* Keyed on the trend: from md up this is its own scroller, and J must
             not open the next trend half way down. */
          <div key={trend.id} className="td-reading">
            {lead}
            {tail}
          </div>
        ) : (
          /* Keyed on the trend: this sheet is one long-lived instance, and J
             swaps the data under a carousel still holding the previous trend's
             position. The key resets the index and the track's scroll offset. */
          <TrendSubmissionCarousel
            key={trend.id}
            examples={trend.examples}
            exampleCount={trendStats(trend).exampleCount}
            standIn={standIn}
            lead={lead}
            tail={tail}
          />
        )}
      </div>
    </Modal>
  );
}
